import React, { useEffect, useState } from 'react';
import { Backdrop, Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { ButtonConfig, confirmationPopupProps } from 'components/common/grid/';
import { TFunction } from 'i18next';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { gridAPI } from 'api/grid';
import IconMi from 'components/common/icon/IconMi';
import { prepareTextDataObject } from 'tools/tools';

type MassDeleteFailedMessageExcludes = {
    message: string;
    items: string[];
};

type MassDeleteFailedMessage = {
    excludes?: MassDeleteFailedMessageExcludes[];
};

type MassDeleteCheckResponse = {
    data: {
        data: {
            success: number[];
            failedMessage?: MassDeleteFailedMessage;
        };
    };
};

interface adjustDeleteConfirmationPopupProps {
    confirmation?: confirmationPopupProps;
    t: TFunction;
    countItems?: number;
    isRemove?: boolean;
    currentItemName?: string | number;
}
export const adjustDeleteConfirmationPopupSettings = (props: adjustDeleteConfirmationPopupProps) => {
    const { confirmation = {}, t, countItems = 1, isRemove = false } = props;
    const typeTrans = isRemove ? 'remove_confirmation_popup' : 'delete_confirmation_popup';

    let itemTypeName = '';
    let itemAssociationsTypeName = '';
    let itemAssociationsTypeNameOne = '';
    if (confirmation?.entityType) {
        itemTypeName = t(`entity_title.${confirmation.entityType}`, { count: countItems });
    }
    if (confirmation?.entityTypeAssociations) {
        itemAssociationsTypeName = t(`entity_title.${confirmation.entityTypeAssociations}`, {
            count: countItems,
        });
        itemAssociationsTypeNameOne = t(`entity_title.${confirmation.entityTypeAssociations}`, {
            count: 1,
        });
    }

    let confirmationPopupTitle = t(`${typeTrans}.title.default`, {
        count: countItems,
    });

    if (confirmation?.title) {
        confirmationPopupTitle = t(confirmation.title, {
            count: countItems,
            item: itemTypeName,
            itemAssociations: itemAssociationsTypeName,
            itemAssociationsOne: itemAssociationsTypeNameOne,
        });
    } else if (confirmation?.entityType) {
        confirmationPopupTitle = t(`${typeTrans}.title.entity_item`, {
            item: itemTypeName,
        });
    }

    return {
        title: confirmationPopupTitle,
        textOK: t(confirmation?.textOK ? t(confirmation.textOK) : `${typeTrans}.ok_btn`),
        needTranslation: false,
        colorOk: confirmation?.colorOk ?? (!isRemove ? 'error' : undefined),
        iconOK: confirmation?.iconOK ?? (!isRemove ? 'trash' : undefined),
        maxWidth: confirmation?.maxWidth !== undefined ? confirmation.maxWidth : 'popupSm',
    };
};
export const adjustDeleteConfirmationPopupMessage = (props: adjustDeleteConfirmationPopupProps) => {
    const { confirmation = {}, t, countItems = 1, isRemove = false, currentItemName = '' } = props;
    const typeTrans = isRemove ? 'remove_confirmation_popup' : 'delete_confirmation_popup';

    let itemTypeName = '';
    let itemTypeNameOne = '';
    let itemAssociationsTypeName = t('remove_confirmation_popup.associations_default');
    let itemAssociationsTypeNameOne = t('remove_confirmation_popup.associations_default');

    if (confirmation?.entityType) {
        itemTypeName = t(`entity_title.${confirmation.entityType}`, { count: countItems });
        itemTypeNameOne = t(`entity_title.${confirmation.entityType}`, { count: 1 });
    }
    if (confirmation?.entityTypeAssociations) {
        itemAssociationsTypeName = t(`entity_title.${confirmation.entityTypeAssociations}`, {
            count: countItems,
        });
        itemAssociationsTypeNameOne = t(`entity_title.${confirmation.entityTypeAssociations}`, {
            count: 1,
        });
    }

    if (confirmation?.text) {
        return t(confirmation.text, {
            count: countItems,
            name: currentItemName,
            item: itemTypeName,
            itemOne: itemTypeNameOne,
            itemAssociations: itemAssociationsTypeName,
            itemAssociationsOne: itemAssociationsTypeNameOne,
        });
    } else if (confirmation?.entityType) {
        return t(`${typeTrans}.text.entity_item`, {
            count: countItems,
            item: itemTypeName,
        });
    } else {
        return t(`${typeTrans}.text.default`, {
            count: countItems,
        });
    }
};

export default function MassDeleteButton({
    config,
    controllerUrl,
    uid,
    selectedItems,
    selectedItemsNames,
    reloadGridData,
    setCheckedKeys,
    t,
    id,
    isRemove,
    gridEntityType,
}: {
    config: ButtonConfig;
    controllerUrl: string;
    uid: string;
    selectedItems: Array<string | number>;
    selectedItemsNames: Array<string | number>;
    reloadGridData?: () => void;
    setCheckedKeys?: (keys: Array<string | number>) => void;
    t: TFunction;
    id: string;
    isRemove: boolean;
    gridEntityType?: string;
}) {
    const deleteUrl = config.url ?? controllerUrl + '/form?mass-delete=true';
    const countItems = selectedItems.length;

    const IGNORE_GRID_ENTITY_TYPE = 'ignore-grid-entity-type';
    const confirmationSettings = { ...(config.confirmation ?? {}) };

    if (gridEntityType && !confirmationSettings.entityType) {
        confirmationSettings.entityType = gridEntityType;
    }
    if (confirmationSettings.entityType === IGNORE_GRID_ENTITY_TYPE) {
        delete confirmationSettings.entityType;
    }

    const adjustConfirmation = () => {
        return adjustDeleteConfirmationPopupMessage({
            confirmation: confirmationSettings,
            t: t,
            countItems: selectedItems.length,
            isRemove: isRemove,
            currentItemName: selectedItemsNames[selectedItemsNames.length - 1],
        });
    };

    const adjustPopupSettings = () => {
        return adjustDeleteConfirmationPopupSettings({
            confirmation: confirmationSettings,
            t: t,
            countItems: selectedItems.length,
            isRemove: isRemove,
        });
    };
    const [isFailedMessage, setIsFailedMessage] = useState<boolean>(false);
    const [successItems, setSuccessItems] = useState<number[]>([]);
    const [remainingItemsText, setRemainingItemsText] = useState<string>('');
    const [failedMessage, setFailedMessage] = useState<MassDeleteFailedMessage | string>({});
    const [open, setOpen] = useState<boolean>(false);
    const [loadingMaskShow, setLoadingMaskShow] = useState<boolean>(false);
    const [commonLoadingMaskShow, setCommonLoadingMaskShow] = useState<boolean>(false);
    const [popupSettings, setPopupSettings] = useState<PopupSettings>(adjustPopupSettings());
    const [confirmation, setConfirmation] = useState<string>(adjustConfirmation());

    useEffect(() => {
        setPopupSettings(adjustPopupSettings());
        setConfirmation(adjustConfirmation());
    }, [isRemove, countItems]);

    const makeMassDelete = () => {
        if (!deleteUrl) {
            return;
        }

        let itemsToDelete: Array<string | number> = config.remoteConfirm ? successItems : selectedItems;
        if (0 == itemsToDelete.length) {
            setOpen(false);
            if (setCheckedKeys) {
                setCheckedKeys([]);
            }
            setIsFailedMessage(false);
            setFailedMessage({});
            setSuccessItems([]);

            return;
        }

        setLoadingMaskShow(true);

        gridAPI
            .massDelete(
                deleteUrl + (config.remoteConfirm ? (-1 === deleteUrl.indexOf('?') ? '?' : '&') + 'confirm=true' : ''),
                { ids: itemsToDelete },
            )
            .then(() => {
                if (reloadGridData) {
                    reloadGridData();
                }
                if (setCheckedKeys) {
                    setCheckedKeys([]);
                }
            })
            .finally(() => {
                setOpen(false);
                setLoadingMaskShow(false);
            });
    };

    const onDeleteClick = () => {
        if (config.remoteConfirm) {
            setCommonLoadingMaskShow(true);
            gridAPI
                .massDelete(deleteUrl, { ids: selectedItems })
                .then((response: MassDeleteCheckResponse) => {
                    if (response.data?.data?.failedMessage) {
                        setIsFailedMessage(true);
                        setFailedMessage(response.data.data.failedMessage);

                        if (0 == response.data?.data?.success?.length) {
                            setSuccessItems([]);
                            setRemainingItemsText('');
                            setPopupSettings({
                                ...popupSettings,
                                noOk: true,
                            });
                        } else {
                            setSuccessItems(response.data.data.success);
                            setRemainingItemsText(
                                t('mass_delete_remaining_confirmation', {
                                    count: response.data.data.success.length,
                                }),
                            );
                        }
                    } else {
                        if (response.data?.data?.success?.length > 0) {
                            setSuccessItems(response.data.data.success);
                        }

                        setIsFailedMessage(false);
                        setFailedMessage({});
                    }

                    setOpen(true);
                })
                .catch(() => {
                    setOpen(false);
                })
                .finally(() => {
                    setCommonLoadingMaskShow(false);
                });
        } else setOpen(true);
    };

    const { getProcessedTranslate: getBtnText } = prepareTextDataObject(config.text ?? 'delete');

    return (
        <Grid item key={config.name} sx={{ flexShrink: 0 }}>
            <Button
                data-test={id}
                onClick={onDeleteClick}
                key={uid + config.name}
                startIcon={<IconMi icon={isRemove ? 'times' : 'delete'} />}
                variant="outlined"
            >
                {getBtnText(t, {
                    entity: gridEntityType ? t(`entity_title.${gridEntityType}`, { count: 1 }) : '',
                    entities: gridEntityType ? t(`entity_title.${gridEntityType}`, { count: 5 }) : '',
                    item: gridEntityType ? t(`entity_title.${gridEntityType}`, { count: selectedItems.length }) : '',
                })}
            </Button>
            <Popup
                settings={popupSettings}
                open={open}
                onHide={() => {
                    setOpen(false);
                    setCheckedKeys && setCheckedKeys([]);
                }}
                onConfirm={makeMassDelete}
                t={t}
            >
                {isFailedMessage ? (
                    typeof failedMessage === 'object' && failedMessage.excludes ? (
                        <div>
                            {failedMessage.excludes?.map((s: MassDeleteFailedMessageExcludes, key: number) => {
                                if (s.items.length) {
                                    return (
                                        <div key={key}>
                                            <span>{s.message}</span>
                                            <ul>
                                                {s.items.map((i: string) => (
                                                    <li>{i}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                } else return '';
                            })}
                            {remainingItemsText ?? ''}
                        </div>
                    ) : (
                        <Box
                            dangerouslySetInnerHTML={{
                                __html: typeof failedMessage === 'string' ? failedMessage : confirmation,
                            }}
                        />
                    )
                ) : (
                    <Typography>{confirmation}</Typography>
                )}
                <Backdrop
                    sx={{ color: 'background.default', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loadingMaskShow}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Popup>
            <Backdrop
                sx={{ color: 'background.default', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={commonLoadingMaskShow}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Grid>
    );
}
